import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatDrawer } from "@angular/material/sidenav";
import { Router } from "@angular/router";
import { SlbLoadingService } from "@slb-dls/angular-material/loading";
import * as moment from "moment";
import { catchError, filter, first, forkJoin, map, Observable, of, pairwise, Subscription, switchMap, tap, throttleTime } from "rxjs";
import { CollectionsService } from "src/app/core/api/collections.service";
import { CompanionDataService } from "src/app/core/api/companion-data.service";
import { ModelService } from "src/app/core/api/model.service";
import { SearchCollectionsResults, SearchCollectionsService, SearchForCollectionsCompanionDataFilters, SearchForCollectionsFilters, SearchForCollectionsModelFilters, SearchForCollectionsRequest } from "src/app/core/api/search-collections.service";
import { autoMonitorPageView } from "src/app/core/decorators/auto-monitor-page-view.decorator";
import { autoUnsubscribe } from "src/app/core/decorators/auto-unsubscribe.decorator";
import { Collection } from "src/app/core/models/collection";
import { ModelVersion } from "src/app/core/models/model-version";
import { Simulation } from "src/app/core/models/export class simulation";
import { MonitoringService } from "src/app/core/services/monitoring.service";
import { CollectionsNeededService } from "../collectionsneeded.service";
import { UpdateCollectionComponent } from "../create-update-collection/update-collection.component";

interface SearchCollectionListItem extends Collection {
  color: string;
  simulationIdsMatched?: string[];
}

export interface CollectionNavigationParams {
  m: string;
  bsid?: string;
  r?: string;
}


@autoMonitorPageView({ name: 'Search Collections', trackOnInitToAfterInit: false })
@autoUnsubscribe({ autoInclude: true })
@Component({
  standalone: false,
  selector: 'app-search-collections-models',
  templateUrl: './search-collections.html',
  styleUrls: ['./search-collections.scss']
})

export class SearchCollectionsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('scroller') scroller: CdkVirtualScrollViewport;
  @ViewChild('drawer') drawer: MatDrawer;
  @ViewChild("pageHeaderOptions")
  private pageHeaderOptionsTpl: TemplateRef<any>;
  listItems: SearchCollectionListItem[] = [];
  nextCursor: { nextPageCollectionsQueryCursor: string, nextPageModelsQueryCursor: string, nextIndexForCompanionDataQuery: number } = null;
  isGrid = true;
  searchText = '';
  currentDate = new Date();
  currentSearchTextSelected = '';
  drawerClosed: boolean = true;
  reset: number = 1;
  isDeletedMode = false;
  selectedIds = new Set<string>();

  companionDataService$: Subscription;

  startDateRange = new FormGroup({
    start: new FormControl(null),
    end: new FormControl(null),
  });

  modifiedDateRange = new FormGroup({
    start: new FormControl(null),
    end: new FormControl(null),
  });

  nameFilterControl = new FormControl('');
  filteredNames: Observable<string[]>;

  createdUserFilterControl = new FormControl('');
  filteredCreatedUsers: Observable<string[]>;

  updatedUserFilterControl = new FormControl('');
  filteredUpdatedUsers: Observable<string[]>;

  separatorKeysCodes: number[] = [ENTER, COMMA];

  selectedOwners: string[] = [];
  ownerFilterControl = new FormControl();
  filteredOwners: Observable<string[]>;

  selectedViewers: string[] = [];
  viewerFilterControl = new FormControl();
  filteredViewers: Observable<string[]>;

  modelNameFilterControl = new FormControl('');
  filteredModelNames: Observable<string[]>;

  genOptFileNameFilterControl = new FormControl('');
  filteredGenOptFileNames: Observable<string[]>;

  genOptFileIdFilterControl = new FormControl('');
  filteredGenOptFileIds: Observable<string[]>;

  remarkFilterControl = new FormControl('');
  filteredRemarks: Observable<string[]>;

  modelVersionNumberFilterControl = new FormControl('');
  filteredModelVersionNumbers: Observable<string[]>;

  businessPlanCaseFilterControl = new FormControl('');
  filteredBusinessPlanCases: Observable<string[]>;

  reservesCaseFilterControl = new FormControl('');
  filteredReservesCases: Observable<string[]>;

  reservoirFilterControl = new FormControl('');
  filteredReservoirs: Observable<string[]>;

  decisionFrameFilterControl = new FormControl('');
  filteredDecisionFrames: Observable<string[]>;

  modelObjectiveFilterControl = new FormControl('');
  filteredModelObjectives: Observable<string[]>;

  projectPhaseFilterControl = new FormControl('');
  filteredProjectPhases: Observable<string[]>;

  fieldFilterControl = new FormControl('');
  filteredFields: Observable<string[]>;

  forecastYearFilterControl = new FormControl('');
  filteredForecastYears: Observable<string[]>;

  simulationApplicationFilterControl = new FormControl('');
  filteredSimulationApplications: Observable<string[]>;

  simulationApplicationVersionFilterControl = new FormControl('');
  filteredSimulationApplicationVersions: Observable<string[]>;

  genOptTemplateUsedFilterControl = new FormControl('');
  filteredGenOptTemplateUsed: Observable<string[]>;

  genOptGeneralUsedFilterControl = new FormControl('');
  filteredGenOptGeneralUsed: Observable<string[]>;

  currentFiltersSelected: {
    collectionFilters?: SearchForCollectionsFilters,
    modelFilters?: SearchForCollectionsModelFilters,
    companionDataFilters?: SearchForCollectionsCompanionDataFilters
  };

  currentFilters: {
    collectionFilters?: SearchForCollectionsFilters,
    modelFilters?: SearchForCollectionsModelFilters,
    companionDataFilters?: SearchForCollectionsCompanionDataFilters
  };


  @ViewChild('ownerFilterInput') ownerFilterInput: ElementRef<HTMLInputElement>;
  @ViewChild('viewerFilterInput') viewerFilterInput: ElementRef<HTMLInputElement>;

  constructor(
    private ngZone: NgZone,
    private oneService: CollectionsNeededService,
    private collectionsService: CollectionsService,
    private modelService: ModelService,
    public loadingService: SlbLoadingService,
    private companionDataService: CompanionDataService,
    private router: Router,
    public dialog: MatDialog,
    private searchCollectionsService: SearchCollectionsService,
    private monitoringService: MonitoringService
  ) {
    this.oneService.breadcrumbsService.setCurrentBreadcrumbItems(
      [
      ]);
  }


  ngOnInit(): void {
    const searchRequest = {
      searchText: '',
      isDeletedMode: this.isDeletedMode
    } as SearchForCollectionsRequest;
    this.fetchMore(true, searchRequest);

  }


  ngAfterViewInit(): void {

    this.oneService.pageHeaderService.setCurrentToolbarTemplate(this.pageHeaderOptionsTpl);
    this.scroller.elementScrolled().pipe(
      map(() => this.scroller.measureScrollOffset('bottom')),
      pairwise(),
      filter(([y1, y2]) => {
        const r = (y2 < y1 && y2 < 140);
        return r;
      }),
      throttleTime(200)
    ).subscribe(() => {
      this.ngZone.run(() => {
        if (this.nextCursor) {
          const searchRequest = Object.assign({
            searchText: this.currentSearchTextSelected,
            isDeletedMode: this.isDeletedMode,
            collectionsQueryCursor: this.nextCursor.nextPageCollectionsQueryCursor,
            modelsQueryCursor: this.nextCursor.nextPageModelsQueryCursor,
            indexOnClient: this.nextCursor.nextIndexForCompanionDataQuery
          }, this.currentFiltersSelected) as SearchForCollectionsRequest;
          this.fetchMore(false, searchRequest);
        }
      });
    }
    );


  }

  _ids: string[] = [];
  _duplicatedIds: string[] = [];

  fetchMore(clearExisting: boolean, searchRequest?: SearchForCollectionsRequest): void {
    this.loadingService.showSpinner();
    this.searchCollectionsService.search(searchRequest).pipe(first(), tap((searchResults: SearchCollectionsResults) => {
      this.loadingService.closeSpinner();

      this.nextCursor = {
        nextPageCollectionsQueryCursor: searchResults.nextPageCollectionsQueryCursor,
        nextPageModelsQueryCursor: searchResults.nextPageModelsQueryCursor,
        nextIndexForCompanionDataQuery: searchResults.nextIndexForCompanionDataQuery
      };
      const existingIds = new Set((clearExisting ? [] : this.listItems).map(x => x.id));
      const seenInBatch = new Set<string>();
      const newItems = searchResults.results
        .filter(x => {
          if (existingIds.has(x.id) || seenInBatch.has(x.id)) return false;
          seenInBatch.add(x.id);
          return true;
        })
        .map(p => Object.assign({ color: p.moniker.split(' ')[0] }, p) as SearchCollectionListItem);

      this.listItems = [...(clearExisting ? [] : this.listItems), ...newItems];
      if (clearExisting) {
        this.selectedIds = new Set<string>();
      }
    })).subscribe();
  }

  onSearch(): void {
    this.currentSearchTextSelected = this.searchText;
    const searchRequest = {
      searchText: this.currentSearchTextSelected,
      isDeletedMode: this.isDeletedMode
    } as SearchForCollectionsRequest;
    this.fetchMore(true, searchRequest);
  }

  onClearSearch(): void {
    this.searchText = '';
    this.currentSearchTextSelected = this.searchText;
    const searchRequest = {
      searchText: '',
      isDeletedMode: this.isDeletedMode
    } as SearchForCollectionsRequest;
    this.fetchMore(true, searchRequest);
  }


  createCollection(): void {

    let dialogRef = this.dialog.open(UpdateCollectionComponent, {
      width: "50vw",
      maxHeight: '100vh',
      panelClass: "dgPanel"
    });

    dialogRef.afterClosed().subscribe(collectionId => {
      if (collectionId) {
        this.router.navigate(["/collection", collectionId]);
      }
    });
  }

  // this should trigger the new search, no old cursor should be used
  toggleDeletedIndicator(): void {
    this.isDeletedMode = !this.isDeletedMode;
    this.currentSearchTextSelected = this.searchText;
    const searchRequest = {
      searchText: this.currentSearchTextSelected,
      isDeletedMode: this.isDeletedMode
    } as SearchForCollectionsRequest;
    this.fetchMore(true, searchRequest);
  }

  onRefresh() {
    const searchRequest = { searchText: '', isDeletedMode: this.isDeletedMode } as SearchForCollectionsRequest;
    this.fetchMore(true, searchRequest);
  }


  ngOnDestroy(): void {
    console.log('on destroy being called for search collections component');
  }

  areInSameMinute(a: Date, b: Date) {
    return moment(a).diff(moment(b), 'minutes') === 0;
  }

  onViewCollection(item: SearchCollectionListItem): void {
    const matchedSimulationId = this.getPrimaryMatchedSimulationId(item);
    if (matchedSimulationId) {
      this.onRelatedSimulationSelected(item, matchedSimulationId, this.currentSearchTextSelected?.trim());
      return;
    }

    this.router.navigate(["/collection", item.id]);
  }

  onRelatedSimulationSelected(item: SearchCollectionListItem, matchedSimulationId: string, searchText?: string): void {
    if (!item?.id || !matchedSimulationId) {
      return;
    }

    this.loadingService.showSpinner({ text: 'Opening matched simulation...' });
    this.resolveNavigationParams(item.id, matchedSimulationId, searchText).pipe(first()).subscribe({
      next: queryParams => {
        this.loadingService.closeSpinner();
        this.router.navigate(["/collection", item.id], { queryParams });
      },
      error: err => {
        console.error(err);
        this.loadingService.closeSpinner();
        this.router.navigate(["/collection", item.id], { queryParams: { m: matchedSimulationId } });
      }
    });
  }

  onModifyCollection(id: string): void {
    const dialogRef = this.dialog.open(UpdateCollectionComponent, {
      width: "50vw",
      maxHeight: '80vh',
      panelClass: "dgPanel",
      data: id
    });

    dialogRef.afterClosed().subscribe(collection => {
      if (collection) {
        console.log("collection exist");
      }
    });
  }

  // recover back/undelete
  onRecoverBack(selectedCollectionId: string): void {
    this.loadingService.showSpinner({ text: "Recoverback Request is being Sent..." });
    this.collectionsService.recoverCollectionsById({ collectionId: selectedCollectionId }).subscribe(recoverResponse => {
      if (recoverResponse.response?.isSuccessStatusCode === true) {
        this.listItems = [...this.listItems.filter(p => p.id !== selectedCollectionId)];
      } else {
        this.oneService.toastrService.error("Could not recover collection!. You might not have permissions")
      }
      this.loadingService.closeSpinner();
    });
  }


  onCollectionClick() {

  }

  toggleSelection(id: string): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
    this.selectedIds = new Set(this.selectedIds);
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  onDeleteSelected(): void {
    this.oneService.dialogService.deleteCollectionConfirmation().subscribe(confirmed => {
      if (confirmed) {
        this.loadingService.showSpinner({ text: 'Deleting selected collections...' });
        const ids = Array.from(this.selectedIds);
        const deleteRequests = ids.map(id =>
          this.collectionsService.deleteCollectionsById({ collectionId: id }).pipe(
            catchError(() => of({ response: { isSuccessStatusCode: false } }))
          )
        );
        forkJoin(deleteRequests).subscribe(responses => {
          const successIds = ids.filter((_, i) => responses[i]?.response?.isSuccessStatusCode === true);
          const failCount = ids.length - successIds.length;
          this.listItems = [...this.listItems.filter(p => !successIds.includes(p.id))];
          successIds.forEach(id => this.selectedIds.delete(id));
          this.selectedIds = new Set(this.selectedIds);
          if (failCount > 0) {
            this.oneService.toastrService.error(`${failCount} collection(s) could not be deleted. You might not have delete permissions.`);
          }
          this.loadingService.closeSpinner();
        });
      }
    });
  }

  onDeleteCollection(selectedCollectionId: string) {
    this.oneService.dialogService.deleteCollectionConfirmation().subscribe(confirmed => {
      if (confirmed) {
        this.loadingService.showSpinner({ text: "Deletion Request is being Sent..." });
        this.collectionsService.deleteCollectionsById({ collectionId: selectedCollectionId }).subscribe(deleteResponse => {
          if (deleteResponse.response?.isSuccessStatusCode === true) {
            this.listItems = [...this.listItems.filter(p => p.id !== selectedCollectionId)];
          } else {
            this.oneService.toastrService.error("Could not delete collection!. You might not have delete permissions")
          }
          this.loadingService.closeSpinner();
        });
      }
    });
  }

  onSlideDrawer(): void {
    if (this.drawerClosed)
      this.drawer.open().then(_ => {
        this.drawerClosed = false;
      });
    else
      this.onCloseDrawer();
  }

  onCloseDrawer(): void {
    this.drawer.close().then(_ => {
      this.drawerClosed = true;
    });
  }

  onFiltersChanged(filters: {
    collectionFilters?: SearchForCollectionsFilters,
    modelFilters?: SearchForCollectionsModelFilters,
    companionDataFilters?: SearchForCollectionsCompanionDataFilters
  }): void {
    this.currentFilters = filters;
  }

  onApplyFilter(): void {
    this.currentFiltersSelected = this.currentFilters;
    const searchRequest = Object.assign({ searchText: this.currentSearchTextSelected, isDeletedMode: this.isDeletedMode },
      this.currentFiltersSelected) as SearchForCollectionsRequest;
    this.fetchMore(true, searchRequest);
  }

  onReset(): void {
    this.reset += 1;
    this.currentFilters = {};
    this.currentFiltersSelected = {};
    const searchRequest = {
      searchText: this.currentSearchTextSelected,
      isDeletedMode: this.isDeletedMode
    } as SearchForCollectionsRequest;
    this.fetchMore(true, searchRequest);
  }

  getToolTipForCollection(coll: Collection): string {
    return `
    Description:${coll.description}
    Owners: ${coll.owners.join(",")}
    Viewers: ${coll.viewers.join(",")}
    Tags: ${(coll.tags ?? []).join(",")}
    `;
  }

  private getPrimaryMatchedSimulationId(item: SearchCollectionListItem): string | null {
    return item?.simulationIdsMatched?.[0] ?? null;
  }

  private resolveNavigationParams(collectionId: string, matchedSimulationId: string, searchText?: string): Observable<CollectionNavigationParams> {
    return this.collectionsService.getSimulationsForCollection(collectionId).pipe(
      first(),
      switchMap((simulations: Simulation[]) => {
        const safeSimulations = simulations ?? [];
        if (safeSimulations.length === 0) {
          return of({ m: matchedSimulationId });
        }

        const preferredId = searchText?.trim();
        const fallbackId = matchedSimulationId;

        return this.resolveNavigationParamsForId(safeSimulations, preferredId).pipe(
          switchMap(preferredMatch => {
            if (preferredMatch) {
              return of(preferredMatch);
            }

            return this.resolveNavigationParamsForId(safeSimulations, fallbackId).pipe(
              map(fallbackMatch => fallbackMatch ?? { m: fallbackId })
            );
          })
        );
      }),
      catchError(() => of({ m: matchedSimulationId }))
    );
  }

  private resolveNavigationParamsForId(simulations: Simulation[], lookupId?: string): Observable<CollectionNavigationParams | null> {
    if (!lookupId) {
      return of(null);
    }

    const byModelId = simulations.find(simulation => simulation.id === lookupId);
    if (byModelId) {
      return of({ m: byModelId.id, bsid: byModelId.branchSimulationId });
    }

    const versionLookups = simulations.map(simulation =>
      this.modelService.getVersions(simulation.id).pipe(
        map((versions: ModelVersion[]) => {
          const match = (versions ?? []).find(version =>
            version.id === lookupId || version.revisionId === lookupId
          );

          return {
            simulation,
            match
          };
        }),
        catchError(() => of({ simulation, match: undefined }))
      )
    );

    return forkJoin(versionLookups).pipe(
      map(results => {
        const matchedResult = results.find(result => !!result.match);
        if (!matchedResult?.match) {
          const byBranchId = simulations.find(simulation => simulation.branchSimulationId === lookupId);
          if (byBranchId) {
            return { m: byBranchId.id, bsid: byBranchId.branchSimulationId };
          }

          return null;
        }

        return {
          m: matchedResult.match.id,
          bsid: matchedResult.simulation.branchSimulationId,
          r: matchedResult.match.revisionId
        };
      })
    );
  }



}